import React from 'react'
import {
  Icon,
  Stack,
  Text,
  IconButton,
  DirectionalHint,
  IColumn,
} from 'office-ui-fabric-react'
import { ChildProps, graphql } from 'react-apollo'
import moment from 'moment'
import {
  OutstreamPlacementResponse,
  OutstreamPlacementInfo,
  getOutstreamPlacementsQuery,
  deleteOutstreamPlacement,
} from '../../Api/outstreamPlacements_v2'
import { TableView } from '../../Components/TableView'
import { CreateOutstreamPlacementDialog } from './CreateOutstreamPlacementDialog'
import { DeletePlacementDialog } from '../../Components/DeletePlacementDialog'
import './OutstreamPlacementList.css'
import { SessionContext } from '../../Contexts/SessionContext'
import {
  addEntityIdToHash,
  clearEntityIdFromHash,
  getEntityIdFromHash,
} from '../../Utils/hash'

interface State {
  openModal?: {
    modalType: 'delete'
    placement: OutstreamPlacementInfo
  }
}

interface InputProps {
  collectionId: string
  cursor: string
}

interface PlacementRowItem extends OutstreamPlacementInfo {
  tagsView: string[]
  type: 'default' | 'custom'
  collectionId: string
}

class OutstreamPlacementListView extends React.Component<
  ChildProps<InputProps, OutstreamPlacementResponse>,
  State
> {
  static contextType = SessionContext
  context!: React.ContextType<typeof SessionContext>

  constructor(params: any) {
    super(params)
    this.state = {
      openModal: undefined,
    }
  }
  render() {
    const { error } = this.props.data!
    if (error) {
      window.console.error(error)
      return this.renderError()
    }
    const assets = this.getAssets()
    const columns = this.getColumns()
    if (assets && assets.length === 0) {
      return this.renderEmpty()
    }
    return (
      <React.Fragment>
        <TableView
          cacheKey={this.props.collectionId}
          columns={columns}
          rowToCacheKey={(row) => row.collectionId}
          rows={assets}
          onRowClick={(row, event: { button: number }) => {
            if (event.button === 1) {
              window.open(
                `${process.env.PUBLIC_URL}/#/${this.context.ctok}/outstream-placements_v2/${row.collectionId}/#/edit/${row.id}`,
                '_blank'
              )
            } else {
              this.editPlacement(row)
            }
          }}
        />
        {this.renderUpdatePlacementDialog(this.props.data!.refetch)}
        {this.renderDeletePlacementDialog(this.props.data!.refetch)}
      </React.Fragment>
    )
  }
  renderError() {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{ width: '100%' }}
      >
        <Text
          variant="xLarge"
          style={{
            maxWidth: 600,
            padding: 20,
            textAlign: 'center',
          }}
        >
          This placement collection could not be found. Most likely, you've
          opened an older link of a collection that is now deleted. If the error
          persists and your collection can not be selected from the navigation
          menu, please contact support.
        </Text>
      </Stack>
    )
  }
  renderEmpty() {
    return (
      <Stack
        horizontalAlign="center"
        verticalAlign="center"
        verticalFill
        style={{ width: '100%' }}
      >
        <Text
          variant="xLarge"
          style={{
            maxWidth: 600,
            padding: 20,
            textAlign: 'center',
          }}
        >
          This placement collection is empty. You can add a placement by
          pressing the <i>Create outstream placement</i>{' '}
          <Icon iconName="BuildQueueNew" style={{ verticalAlign: 'middle' }} />
          &nbsp;button.
        </Text>
      </Stack>
    )
  }
  getAssets() {
    const placementCollections =
      this.props.data!.outstreamPlacementCollections_v2!
    const placementCollection = placementCollections && placementCollections[0]
    const apiAssets = placementCollection && placementCollection.assets
    if (!apiAssets) {
      return
    }
    return apiAssets.map((a) => ({
      ...a,
      tagsView: a.tags.filter((t) => !t.startsWith('opz')),
      type: a.integration ? 'default' : 'custom',
      collectionId: placementCollection.id,
    }))
  }
  getColumns(): IColumn[] {
    return [
      {
        key: 'type',
        name: '',
        fieldName: 'type',
        minWidth: 20,
        maxWidth: 20,
        isRowHeader: true,
        isIconOnly: true,
        isPadded: false,
        onRender: (a: PlacementRowItem) => (
          <Icon
            iconName={
              a.type === 'default'
                ? 'ArrangeBringToFront'
                : 'HighlightMappedShapes'
            }
            style={{
              fontSize: 14,
              paddingLeft: 5,
              paddingTop: 3,
            }}
          />
        ),
      },
      {
        key: 'integration',
        name: 'Integration',
        fieldName: 'integration',
        isRowHeader: true,
        isPadded: true,
        minWidth: 150,
        maxWidth: 200,
        onRender: (a: PlacementRowItem) => (
          <span title={a.integration}>{a.integration}</span>
        ),
      },
      {
        key: 'name',
        name: 'Name',
        fieldName: 'title',
        isRowHeader: true,
        isPadded: true,
        minWidth: 150,
        maxWidth: 350,
        onRender: (a: PlacementRowItem) => (
          <span title={a.title}>{a.title}</span>
        ),
      },
      {
        key: 'publishDate',
        name: 'Publish Date',
        fieldName: 'creationDate',
        minWidth: 100,
        maxWidth: 100,
        isPadded: true,
        onRender: (a: PlacementRowItem) => (
          <span>{moment(a.creationDate).format('DD/MM/YYYY')}</span>
        ),
      },
      {
        key: 'tags',
        name: 'Tags',
        fieldName: 'tagsView',
        minWidth: 150,
        maxWidth: 500,
        isPadded: true,
        onRender: (a: PlacementRowItem) => <span>{a.tagsView.join(', ')}</span>,
      },
      {
        key: 'menu',
        name: '',
        fieldName: '',
        minWidth: 20,
        maxWidth: 20,
        isIconOnly: true,
        onRender: (a: PlacementRowItem) => (
          <IconButton
            styles={{
              root: {
                height: 18,
              },
              menuIcon: {
                fontWeight: 'bold',
              },
            }}
            menuProps={{
              coverTarget: true,
              directionalHint: DirectionalHint.topCenter,
              items: [
                {
                  key: 'edit',
                  iconProps: { iconName: 'edit' },
                  text: 'Edit',
                  onClick: () => this.editPlacement(a),
                },
                {
                  key: 'delete',
                  iconProps: { iconName: 'delete' },
                  text: 'Delete',
                  onClick: () => this.deletePlacement(a),
                },
              ],
            }}
          />
        ),
      },
    ]
  }
  editPlacement(placement: OutstreamPlacementInfo) {
    addEntityIdToHash(placement.id)
  }
  deletePlacement(placement: OutstreamPlacementInfo) {
    this.setState({
      openModal: {
        modalType: 'delete',
        placement,
      },
    })
  }
  renderDeletePlacementDialog(refetch: () => void) {
    if (!this.state.openModal || this.state.openModal!.modalType !== 'delete') {
      return <React.Fragment />
    }
    return (
      <DeletePlacementDialog
        type="outstream"
        title={this.state.openModal!.placement.title}
        deletePlacement={() =>
          deleteOutstreamPlacement(
            this.props.collectionId,
            this.state.openModal!.placement.id
          )
        }
        onUpdated={() => refetch()}
        onClose={() => this.setState({ openModal: undefined })}
      />
    )
  }
  renderUpdatePlacementDialog(refetch: () => void) {
    const id = getEntityIdFromHash()
    if (!id || !this.getAssets()) {
      return null
    }
    const placement = this.getAssets()!.find((p) => p.id === id)
    if (!placement) {
      return null
    }
    return (
      <CreateOutstreamPlacementDialog
        collectionId={this.props.collectionId}
        modifiedPlacement={placement}
        onUpdated={() => refetch()}
        onClose={() => clearEntityIdFromHash()}
      />
    )
  }
}

const withGql = graphql<InputProps, OutstreamPlacementResponse>(
  getOutstreamPlacementsQuery,
  {
    options: ({ collectionId, cursor }) => ({
      variables: {
        collectionId,
        startFromId: cursor,
      },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
    }),
  }
)
const OutstreamPlacementList = withGql(OutstreamPlacementListView)
export { OutstreamPlacementList }
